import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { LoadingState, TrustBadge, EmptyState } from "@/components/shared";
import { JobDetail } from "@/components/JobDetail";
import { sectionPath } from "@/components/job/Lifecycle";
import { goTop } from "@/lib/embedded";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, FileText, BadgeCheck, Eye, ExternalLink,
  ListChecks, GraduationCap, Loader2, Pencil, Save, Plus, X, Link2, Check, MessageSquare,
} from "lucide-react";

const blankDraft = (job) => ({
  title: job.title === "Untitled role" ? "" : job.title || "",
  department: job.department || "",
  location: job.location || "",
  seniority: job.seniority || "",
  employment_type: job.employment_type || "Full-time",
  summary: job.summary || "",
  responsibilities: [...(job.responsibilities || [])],
  requirements: [...(job.requirements || [])],
  competencies: (job.competencies || []).map((c) => ({ ...c })),
});

// The Role step: the role spec on its own; the conversation lives in Copilot ("Open in chat" in the job header).
export default function JobBuilder({ onChanged }) {
  const { jobId } = useParams();
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [signoffs, setSignoffs] = useState([]);
  const [signoffFilter, setSignoffFilter] = useState(null);
  const [showLinks, setShowLinks] = useState(false);
  const [links, setLinks] = useState([]);
  const [expiryDays, setExpiryDays] = useState(14);
  const [linkLabel, setLinkLabel] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => { api.get(`/jobs/${jobId}/preview-signoffs`).then((r) => setSignoffs(r.data || [])).catch(() => {}); }, [jobId, showLinks]);

  const loadLinks = async () => {
    try { const { data } = await api.get(`/jobs/${jobId}/preview-links`); setLinks(data || []); } catch { setLinks([]); }
  };
  const createLink = async () => {
    setCreating(true);
    try {
      const { data } = await api.post(`/jobs/${jobId}/preview-link`, { days: expiryDays, label: linkLabel });
      const url = `${window.location.origin}${data.path}`;
      await navigator.clipboard.writeText(url);
      toast.success("Preview link created & copied", { description: `${linkLabel ? `“${linkLabel}” · ` : ""}Active for ${data.days} day(s) · no login required.` });
      setLinkLabel("");
      loadLinks();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create a preview link");
    } finally { setCreating(false); }
  };
  const revokeLink = async (id) => {
    try { await api.post(`/preview-links/${id}/revoke`); toast.success("Link revoked"); loadLinks(); }
    catch { toast.error("Could not revoke link"); }
  };
  const copyLink = async (id) => {
    try {
      const { data } = await api.post(`/jobs/${jobId}/preview-link`, { days: expiryDays });
      await navigator.clipboard.writeText(`${window.location.origin}${data.path}`);
      toast.success("A fresh copy of this preview link was copied");
      loadLinks();
    } catch { toast.error("Could not copy"); }
  };
  void copyLink;

  useEffect(() => {
    api.get(`/jobs/${jobId}`).then((j) => setJob(j.data)).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [jobId]);

  const startEdit = () => { setDraft(blankDraft(job)); setEditing(true); };
  const cancelEdit = () => { setEditing(false); setDraft(null); };

  const saveEdits = async () => {
    if (!draft.title.trim()) { toast.error("Give the role a title before saving"); return; }
    setSaving(true);
    try {
      const payload = {
        ...draft,
        competencies: draft.competencies.map((c) => ({ ...c, weight: Number(c.weight) || 0 })),
      };
      const { data } = await api.patch(`/jobs/${jobId}`, payload);
      setJob(data);
      setEditing(false);
      setDraft(null);
      onChanged?.();
      toast.success("Job details saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save changes");
    } finally { setSaving(false); }
  };

  if (loading) return <LoadingState label="Opening job builder…" />;
  if (notFound || !job) {
    return (
      <div className="min-h-[50dvh] flex items-center justify-center p-6">
        <EmptyState icon={FileText} title="Job not found"
          description="This role may have been deleted or you don't have access to it."
          action={<Button className="mt-2 gap-1.5" onClick={() => nav("/recruiter/jobs")} data-testid="back-to-jobs-empty"><ArrowLeft className="h-4 w-4" /> Back to jobs</Button>} />
      </div>
    );
  }

  const empty = !job.title || job.title === "Untitled role";
  const provenance = job.provenance === "manual" ? "Manually authored · you're in control" : job.provenance === "ai" ? "AI-drafted · you're in control" : "Authorship unverified · you're in control";

  return (
    <div className="app-container py-6 space-y-5" data-testid="job-builder">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3" data-testid="role-toolbar">
          {!empty && !editing ? <TrustBadge type="ai">{provenance}</TrustBadge> : <span />}
          <div className="flex flex-wrap items-center gap-2">
            {editing ? (
              <>
                <Button size="sm" variant="ghost" onClick={cancelEdit} disabled={saving} data-testid="cancel-edit">Cancel</Button>
                <Button size="sm" onClick={saveEdits} disabled={saving} data-testid="save-job" className="gap-1.5">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                </Button>
              </>
            ) : (
              <>
                {!empty && (
                  <Button size="sm" variant="ghost" onClick={() => setShowPreview(true)} data-testid="preview-candidate" className="gap-1.5">
                    <Eye className="h-4 w-4" /> Preview
                  </Button>
                )}
                {!empty && (
                  <Button size="sm" variant="ghost" onClick={() => { setShowLinks(true); loadLinks(); }} data-testid="share-preview" className="gap-1.5">
                    <Link2 className="h-4 w-4" /> Share preview
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={startEdit} data-testid="edit-job" className="gap-1.5">
                  <Pencil className="h-4 w-4" /> {empty ? "Write manually" : "Edit"}
                </Button>
                {job.lifecycle?.role_complete && (
                  <Button size="sm" onClick={() => goTop(nav, sectionPath(jobId, "test"))} data-testid="role-continue" className="gap-1.5">
                    Continue to assessment <ArrowRight className="h-4 w-4" />
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
        {signoffs.length > 0 && !editing && (() => {
          const labels = [...new Set(signoffs.map((s) => s.link_label).filter(Boolean))];
          const shown = signoffFilter ? signoffs.filter((s) => s.link_label === signoffFilter) : signoffs;
          return (
          <div className="hs-card px-4 py-3 space-y-2" data-testid="preview-signoffs">
            {labels.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5" data-testid="signoff-filter-bar">
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Filter by link:</span>
                {labels.map((lbl) => (
                  <button key={lbl} onClick={() => setSignoffFilter(signoffFilter === lbl ? null : lbl)} data-testid={`signoff-filter-${lbl}`}
                    className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border transition-colors ${signoffFilter === lbl ? "border-primary bg-primary/15 text-primary" : "border-border bg-card text-muted-foreground hover:border-primary/50"}`}>
                    <Link2 className="h-3 w-3" /> {lbl}
                  </button>
                ))}
                {signoffFilter && (
                  <button onClick={() => setSignoffFilter(null)} data-testid="signoff-filter-clear" className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-border text-muted-foreground hover:text-foreground">
                    <X className="h-3 w-3" /> Clear
                  </button>
                )}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Reviewer feedback{signoffFilter ? ` · ${shown.length}` : ""}:</span>
            {shown.slice(0, 6).map((s) => (
              <span key={s.id} title={s.comment || ""} data-testid={`signoff-${s.id}`}
                className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full border ${s.decision === "approved" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-amber-500/30 bg-amber-500/10 text-amber-300"}`}>
                {s.decision === "approved" ? <Check className="h-3 w-3" /> : <MessageSquare className="h-3 w-3" />}
                {s.reviewer} {s.decision === "approved" ? "approved" : "requested changes"}{s.comment ? " · note" : ""}
                {s.link_label ? <button onClick={() => setSignoffFilter(signoffFilter === s.link_label ? null : s.link_label)} className="ml-1 pl-1.5 border-l border-current/30 opacity-80 hover:opacity-100" data-testid={`signoff-label-${s.id}`}>{s.link_label}</button> : null}
              </span>
            ))}
            </div>
          </div>
          );
        })()}
      </div>

      <div className="max-w-4xl min-w-0 space-y-6" data-testid="artifact-pane">
          {editing ? (
            <EditForm draft={draft} setDraft={setDraft} saving={saving} onSave={saveEdits} onCancel={cancelEdit} />
          ) : empty ? (
            <div className="py-10 flex items-center justify-center">
              <EmptyState icon={FileText} title="Your job spec appears here"
                description="Use Open in chat to draft it with Copilot, or write it yourself."
                action={<Button className="mt-2 gap-1.5" variant="outline" onClick={startEdit} data-testid="start-manual"><Pencil className="h-4 w-4" /> Write it manually</Button>} />
            </div>
          ) : (
            <JobDetail job={job} variant="recruiter" />
          )}
      </div>
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="candidate-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm text-muted-foreground font-normal">
              <Eye className="h-4 w-4" /> Candidate preview
              {job.status !== "published" && <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300">Draft — not yet public</span>}
            </DialogTitle>
            <DialogDescription className="sr-only">The candidate-facing view of this role, exactly as applicants see it before you publish.</DialogDescription>
          </DialogHeader>
          <JobDetail job={job} variant="candidate" />
          <div className="rounded-xl border border-border bg-card p-4 mt-2 space-y-2">
            {job.status === "published" ? (
              <a href={`/careers/${job.id}`} target="_blank" rel="noopener noreferrer" data-testid="open-public-page" className="flex items-center justify-center gap-1.5 w-full rounded-md bg-primary text-primary-foreground text-sm font-medium py-2 hover:opacity-90 transition-opacity">
                Open public page <ExternalLink className="h-4 w-4" />
              </a>
            ) : (
              <Button className="w-full gap-1.5" disabled data-testid="preview-apply">Apply now <ArrowRight className="h-4 w-4" /></Button>
            )}
            <p className="text-[11px] text-center text-muted-foreground">
              {job.status === "published" ? "This is your live candidate view — share the public link anywhere." : "This is exactly what candidates see. Publish to make it live."}
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showLinks} onOpenChange={setShowLinks}>
        <DialogContent className="max-w-lg" data-testid="preview-link-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Link2 className="h-4 w-4 text-primary" /> Share draft preview</DialogTitle>
            <DialogDescription>Create a no-login link so a teammate can review this role and sign off. Set how long it stays live and revoke any link instantly.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="text-xs text-muted-foreground mb-1.5">Label (optional — helps you attribute views & sign-offs)</div>
              <input value={linkLabel} onChange={(e) => setLinkLabel(e.target.value)} placeholder="e.g. For Priya" maxLength={80} data-testid="preview-link-label"
                className="w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm outline-none focus:border-primary" />
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1.5">Link stays live for</div>
              <div className="inline-flex rounded-md border border-border overflow-hidden" data-testid="preview-expiry">
                {[1, 7, 14, 30].map((d) => (
                  <button key={d} onClick={() => setExpiryDays(d)} data-testid={`preview-expiry-${d}`} className={`px-3 py-1.5 text-xs transition-colors ${expiryDays === d ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{d}d</button>
                ))}
              </div>
            </div>
            <Button onClick={createLink} disabled={creating} data-testid="preview-link-create" className="gap-1.5 w-full">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Create & copy link
            </Button>
            <div className="space-y-2 max-h-56 overflow-y-auto" data-testid="preview-link-list">
              {links.length === 0 && <p className="text-xs text-muted-foreground">No preview links yet.</p>}
              {links.map((lk) => (
                <div key={lk.id} className="flex items-center justify-between gap-2 rounded-lg border border-border bg-card p-2.5" data-testid={`preview-link-${lk.id}`}>
                  <div className="min-w-0">
                    <div className="text-xs font-medium flex items-center gap-1.5">
                      <span className={`inline-block h-1.5 w-1.5 rounded-full ${lk.status === "active" ? "bg-emerald-400" : lk.status === "revoked" ? "bg-rose-400" : "bg-amber-400"}`} />
                      {lk.label ? <span className="truncate max-w-[140px]">{lk.label}</span> : <span className="capitalize">{lk.status}</span>}
                      <span className="text-muted-foreground">· expires {String(lk.expires_at || "").slice(0, 10)}</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate" data-testid={`preview-link-views-${lk.id}`}>
                      {(lk.views || 0)} view{(lk.views || 0) === 1 ? "" : "s"}{lk.last_viewed_at ? ` · last opened ${String(lk.last_viewed_at).slice(0, 10)}` : ""} · {lk.days}d link
                    </div>
                  </div>
                  {lk.status === "active" && (
                    <Button size="sm" variant="ghost" onClick={() => revokeLink(lk.id)} data-testid={`preview-link-revoke-${lk.id}`} className="gap-1 text-rose-400 hover:text-rose-300 shrink-0"><X className="h-3.5 w-3.5" /> Revoke</Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="glass-card rounded-xl p-5 space-y-3">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <Icon className="h-4 w-4 text-primary" /> {title}
      </div>
      {children}
    </div>
  );
}

function ListEditor({ items, onChange, placeholder, testid }) {
  const set = (i, v) => onChange(items.map((x, idx) => (idx === i ? v : x)));
  const add = () => onChange([...items, ""]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="flex gap-2">
          <Input value={it} onChange={(e) => set(i, e.target.value)} placeholder={placeholder} data-testid={`${testid}-item-${i}`} />
          <Button size="icon" variant="ghost" onClick={() => remove(i)} data-testid={`${testid}-remove-${i}`}><X className="h-4 w-4" /></Button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={add} className="gap-1.5" data-testid={`${testid}-add`}><Plus className="h-3.5 w-3.5" /> Add</Button>
    </div>
  );
}

function CompetencyEditor({ items, onChange }) {
  const set = (i, key, v) => onChange(items.map((c, idx) => (idx === i ? { ...c, [key]: v } : c)));
  const add = () => onChange([...items, { name: "", weight: 0, description: "" }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const total = items.reduce((s, c) => s + (Number(c.weight) || 0), 0);
  return (
    <div className="space-y-3">
      {items.map((c, i) => (
        <div key={i} className="rounded-lg border border-border p-3 space-y-2">
          <div className="flex gap-2">
            <Input value={c.name} onChange={(e) => set(i, "name", e.target.value)} placeholder="Competency name" data-testid={`comp-name-${i}`} />
            <div className="relative w-24 shrink-0">
              <Input type="number" min="0" max="100" value={c.weight} onChange={(e) => set(i, "weight", e.target.value)} className="pr-6" data-testid={`comp-weight-${i}`} />
              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
            </div>
            <Button size="icon" variant="ghost" onClick={() => remove(i)} data-testid={`comp-remove-${i}`}><X className="h-4 w-4" /></Button>
          </div>
          <Input value={c.description || ""} onChange={(e) => set(i, "description", e.target.value)} placeholder="What this competency measures (optional)" data-testid={`comp-desc-${i}`} />
        </div>
      ))}
      <div className="flex items-center justify-between">
        <Button size="sm" variant="outline" onClick={add} className="gap-1.5" data-testid="comp-add"><Plus className="h-3.5 w-3.5" /> Add competency</Button>
        <span className={`text-xs font-mono ${total === 100 ? "text-emerald-400" : "text-amber-400"}`} data-testid="comp-total">Weights total: {total}%</span>
      </div>
    </div>
  );
}

function EditForm({ draft, setDraft, saving, onSave, onCancel }) {
  const upd = (patch) => setDraft((d) => ({ ...d, ...patch }));
  return (
    <div className="space-y-6" data-testid="job-edit-form">
      <div className="flex items-center justify-between gap-2">
        <TrustBadge type="medium">Editing spec</TrustBadge>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving} data-testid="cancel-edit-inline">Cancel</Button>
          <Button size="sm" onClick={onSave} disabled={saving} className="gap-1.5" data-testid="save-job-inline">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="jb-title">Job title</Label>
        <Input id="jb-title" value={draft.title} onChange={(e) => upd({ title: e.target.value })} placeholder="e.g. Senior Backend Engineer" data-testid="edit-title" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="jb-dept">Department</Label>
          <Input id="jb-dept" value={draft.department} onChange={(e) => upd({ department: e.target.value })} placeholder="Engineering" data-testid="edit-department" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="jb-loc">Location</Label>
          <Input id="jb-loc" value={draft.location} onChange={(e) => upd({ location: e.target.value })} placeholder="Remote · Berlin" data-testid="edit-location" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="jb-sen">Seniority</Label>
          <Input id="jb-sen" value={draft.seniority} onChange={(e) => upd({ seniority: e.target.value })} placeholder="Senior" data-testid="edit-seniority" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="jb-emp">Employment type</Label>
          <Input id="jb-emp" value={draft.employment_type} onChange={(e) => upd({ employment_type: e.target.value })} placeholder="Full-time" data-testid="edit-employment" />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="jb-sum">Summary</Label>
        <Textarea id="jb-sum" value={draft.summary} onChange={(e) => upd({ summary: e.target.value })} rows={4} placeholder="A short, compelling overview of the role…" data-testid="edit-summary" />
      </div>

      <Section icon={ListChecks} title="Responsibilities">
        <ListEditor items={draft.responsibilities} onChange={(v) => upd({ responsibilities: v })} placeholder="Add a responsibility" testid="resp" />
      </Section>

      <Section icon={GraduationCap} title="Requirements">
        <ListEditor items={draft.requirements} onChange={(v) => upd({ requirements: v })} placeholder="Add a requirement" testid="req" />
      </Section>

      <Section icon={BadgeCheck} title="Scoring competencies">
        <p className="text-xs text-muted-foreground mb-3">Weighted competencies drive explainable candidate scoring. Aim for a total of 100%.</p>
        <CompetencyEditor items={draft.competencies} onChange={(v) => upd({ competencies: v })} />
      </Section>

      <div className="flex justify-end gap-2 pt-2 border-t border-border">
        <Button variant="ghost" onClick={onCancel} disabled={saving} data-testid="cancel-edit-bottom">Cancel</Button>
        <Button onClick={onSave} disabled={saving} className="gap-1.5" data-testid="save-job-bottom">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
        </Button>
      </div>
    </div>
  );
}
